import toast from "react-hot-toast";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";

type OpenTabFile = {
  id: string;
  name: string;
  metadataType: string;
};

/**
 * Add / remove the active editor file in the MULTI-AGENT workspace.
 *
 * The dev-agent EditorWorkspacePinActions wrote to `useDevAgentStore`, so in the
 * multi-agent IDE clicking "Add to Workspace" did nothing. This writes to the
 * multi-agent store (addArtifact/removeArtifact) so the file shows in the
 * Workspace panel, tagged to the current run.
 */
export function MultiAgentEditorWorkspacePinActions({
  activeFile,
  initialContentById,
  busy,
}: {
  activeFile: OpenTabFile | undefined;
  initialContentById: Record<string, string>;
  orgId?: string | undefined;
  busy: boolean;
}) {
  const fileId = String(activeFile?.id ?? "");
  const inWorkspace = useMultiAgentStore(
    (s) => !!fileId && s.artifacts.some((a) => a.id === fileId),
  );
  if (!activeFile) return null;

  const add = () => {
    const type = activeFile.metadataType === "ApexTrigger" ? "apex_trigger" : "apex_class";
    const content = initialContentById[fileId] || "";
    useMultiAgentStore.getState().addArtifact({
      id: fileId,
      type,
      name: activeFile.name,
      toolName: "editor.add_to_workspace",
      byteSize: content.length,
      createdAt: Date.now(),
      runId: useMultiAgentStore.getState().runId ?? undefined,
      intake: null,
    });
    useMultiAgentIdeStore.getState().setArtifactBadge(fileId, "M");
    toast.success(`${activeFile.name} added to workspace`);
  };

  const remove = () => {
    useMultiAgentStore.getState().removeArtifact(fileId);
    toast.success(`${activeFile.name} removed from workspace`);
  };

  return (
    <div className="flex items-center gap-1.5">
      {inWorkspace ? (
        <button
          type="button"
          onClick={remove}
          disabled={busy}
          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          − Remove from Workspace
        </button>
      ) : (
        <button
          type="button"
          onClick={add}
          disabled={busy}
          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/20 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          + Add to Workspace
        </button>
      )}
    </div>
  );
}
